/**
 * Is this ticket's customer / product settled? (P30, Roman's decision 6)
 *
 * The question was already answered in `TicketDetail.tsx` — `customerUnsettled`
 * / `productUnsettled` decided whether the P21 panel appears. P30 has to ask it
 * on two more surfaces (`/tickets` and `/review`, once per row), and a second
 * copy of the rule would be a board that marks a ticket "not identified" while
 * the ticket itself offers no panel to identify it. So the rule moved here and
 * the detail screen reads it back.
 *
 * The three states are Roman's, not a UI invention:
 *
 *   identified     — an ERP id is set and nothing disputes it.
 *   notIdentified  — no id at all. What the screen shows are the words of the
 *                    mail; nothing can be authorised or drafted from them.
 *   toConfirm      — an id IS set and the ticket still carries an open question
 *                    of that family. Live on 2026-09-02 this was 0 tickets, and
 *                    it is modelled anyway because the state is reachable the
 *                    moment a run resolves a fact it is unsure about.
 */

import type { EntityState } from '../components/ui/EntityLinks';
import { reviewReasonList } from './labels';

/**
 * The review reasons the two P21 panels answer. Mirrors
 * `CUSTOMER_ANSWERED_REASONS` / `PRODUCT_ANSWERED_REASONS` in
 * `api/src/agent/steps/recompute.ts` — the server clears exactly these when the
 * fact is set, so the screen must offer the panel for exactly these.
 */
export const CUSTOMER_REVIEW_REASONS = ['CustomerUnresolved', 'CustomerAmbiguous', 'LowConfidence:customer', 'AuthCustomerUnresolved'];
export const PRODUCT_REVIEW_REASONS = ['ProductUnresolved', 'ProductAmbiguous', 'LowConfidence:product', 'AuthProductUnresolved'];

interface FactBearingTicket {
    customerId: string | null;
    productId: string | null;
    reviewReason: string | null;
}

function stateOf(id: string | null, reasons: string[], disputing: string[]): EntityState {
    if (id === null) return 'notIdentified';
    return reasons.some(r => disputing.includes(r)) ? 'toConfirm' : 'identified';
}

export function customerState(t: FactBearingTicket): EntityState {
    return stateOf(t.customerId, reviewReasonList(t.reviewReason), CUSTOMER_REVIEW_REASONS);
}

export function productState(t: FactBearingTicket): EntityState {
    return stateOf(t.productId, reviewReasonList(t.reviewReason), PRODUCT_REVIEW_REASONS);
}

/** "Unsettled" is the union of the two non-plain states — what the panels gate on. */
export function isUnsettled(state: EntityState): boolean {
    return state !== 'identified';
}
